import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import {
  Navigation,
  Mousewheel,
  EffectFade,
  Autoplay,
  // Virtual
} from "swiper";

import "swiper/css";
import "swiper/css/effect-fade";
// import "swiper/css/virtual";
import styled from "styled-components";
import { useInView } from "react-intersection-observer";
import { deviceType } from "detect-it";

const uA =
  typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : "";
const isAndroid = uA.indexOf("android") !== -1;
const isChrome = uA.indexOf("chrome") !== -1;
const isSafari = (uA.indexOf("safari") !== -1 || uA.indexOf("applewebkit") !== -1 || uA.indexOf("ipad") !== -1 || uA.indexOf("iphone") !== -1) && !isChrome;
const totalPixel =
  typeof window !== "undefined"
    ? window.screen.width + window.screen.height
    : 2000;

const SlideshowContainer = styled.aside<{ isFullscreen: boolean }>`
  height: 100%;
  width: 100%;
  & .swiper {
    height: 100%;
    width: 100%;
  }
  & .swiper-slide,
  & .swiper-backface-hidden .swiper-slide {
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    transform: translateZ(0);
  }
`;

const Slide = styled.div<{ isFullscreen: boolean }>`
  height: 100%;
  width: 100%;
  user-select: none;
  background-color: var(--ikon-bg-color, #fff);
  box-sizing: border-box;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          padding: ${
            props.isFullscreen ? props.theme.spacePx(breakpoint, 7) : 0
          };
          
        `;
    })}
`;

const SwiperContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

export const ExhibitionSlideShow = ({
  slides,
  buttonPrevRef,
  buttonNextRef,
  isFullscreen,
  slidesVirtualMin,
}: {
  slides: React.ReactNode[];
  buttonPrevRef: React.MutableRefObject<HTMLButtonElement>;
  buttonNextRef: React.MutableRefObject<HTMLButtonElement>;
  isFullscreen: boolean;
  slidesVirtualMin: number;
}) => {
  const swiperRef = useRef<SwiperType>();
  const inView = useInView({
    fallbackInView: true
  });
  const [swiperKey, setSwiperKey] = useState("exhibition");

  useEffect(() => {
    if (typeof window === "undefined" || !slides?.length) return;

    setSwiperKey(isFullscreen ? "exhibition-fullscreen" : "exhibition");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  useEffect(() => {
    if (typeof window === "undefined" || !swiperRef.current) return;

    if (!isFullscreen) {
      if (inView.inView) {
        swiperRef.current.autoplay.start();
      } else {
        swiperRef.current.autoplay.stop();
      }
    }
  }, [inView.inView, isFullscreen]);

  if (!Array.isArray(slides) || slides.length === 0) return <></>;

  const sliceAt =
    deviceType !== "mouseOnly"
      ? isAndroid || isSafari
        ? totalPixel < 1700
          ? 10
          : 15
        : 100
      : 100;

  return (
    <SlideshowContainer isFullscreen={isFullscreen} ref={inView.ref}>
      <SwiperContainer>
        <Swiper
          onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
          navigation={{
            prevEl: buttonPrevRef.current ?? null,
            nextEl: buttonNextRef.current ?? null,
          }}
          onInit={() => {
            if (!isFullscreen) {
              if (buttonPrevRef.current) {
                buttonPrevRef.current.removeAttribute("disabled");
              }

              if (buttonNextRef.current) {
                buttonNextRef.current.removeAttribute("disabled");
              }
            }
          }}
          effect={
            !isFullscreen || slides.length > slidesVirtualMin ? "slide" : "fade"
          }
          speed={750}
          autoplay={
            !isFullscreen
              ? {
                  delay: 4500,
                  disableOnInteraction: true,
                }
              : false
          }
          cssMode={false}
          loop={slides.length <= slidesVirtualMin || deviceType === "mouseOnly"}
          // virtual={
          //   slides.length > slidesVirtualMin
          // }
          /// modules={[Autoplay, Navigation, EffectFade, Mousewheel, Virtual]}
          modules={[Autoplay, Navigation, EffectFade, Mousewheel]}
          spaceBetween={0}
          grabCursor={true}
          edgeSwipeDetection
          centeredSlides={true}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1,
            thresholdDelta: 60,
          }}
          slidesPerView={1}
          // slidesPerView={!isFullscreen && slides.length <= slidesVirtualMin ? "auto" : 1}
          // changing key forces an update of the component ...
          key={swiperKey}
        >
          {slides.slice(0, sliceAt).map((slide, index: number) => (
            <SwiperSlide key={`swiper-${index}`} virtualIndex={index}>
              <Slide
                key={`slideshow-slide-${index}`}
                className={
                  index === 0
                    ? "first"
                    : index === slides.length - 1
                    ? "last"
                    : ""
                }
                isFullscreen={isFullscreen}
              >
                {slide}
              </Slide>
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperContainer>
    </SlideshowContainer>
  );
};

export default ExhibitionSlideShow;
